import { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingDown, TrendingUp } from 'lucide-react';
import api from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import '../AllCss/PriceHistoryChart.css';

function formatChartDate(date) {
  const d = new Date(date);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function formatPrice(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function PriceHistoryChart({ carId }) {
  const { t } = useLocale();
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!carId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);

    api
      .get(`/cars/${carId}/price-history`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success && res.data.data?.points?.length > 1) {
          setPoints(
            res.data.data.points.map((p) => ({
              label: formatChartDate(p.date),
              price: p.price,
            }))
          );
        } else {
          setPoints([]);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [carId]);

  if (loading || error || points.length < 2) return null;

  const first = points[0].price;
  const last = points[points.length - 1].price;
  const diff = last - first;
  const dropped = diff < 0;

  return (
    <section className="price-history-chart" aria-label={t('carDetails.priceHistoryTitle')}>
      <div className="price-history-chart-header">
        <h3>{t('carDetails.priceHistoryTitle')}</h3>
        <span className={`price-history-trend ${dropped ? 'down' : 'up'}`}>
          {dropped ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
          {dropped ? t('carDetails.priceWentDown') : t('carDetails.priceWentUp')}
          {' '}
          {formatPrice(Math.abs(diff))}
        </span>
      </div>
      <div className="price-history-chart-body">
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              width={48}
            />
            <Tooltip formatter={(v) => formatPrice(v)} labelFormatter={(l) => l} />
            <Line
              type="monotone"
              dataKey="price"
              stroke="var(--primary-600, #e06509)"
              strokeWidth={2}
              dot={{ r: 3, fill: 'var(--primary-600, #e06509)' }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

export default PriceHistoryChart;
